import React from "react";
import { Box, MenuItem, Separator, Switch } from "@showitinc/showit-wc";
import ContextMenuWrapper from "./ContextMenuWrapper";
import When from "../../components/utils/When";
import uiPrefix from "../../utils/UiPrefixUtils";
import AppConstants from "../../constants/AppConstants";
import SiteUtils from "../../utils/SiteUtils";
import { copyToClipboard } from "../../utils/AppUtils";
import UserEventConstants from "../../constants/UserEventConstants";

import UIActions from "../../actions/UIActions";
import DocumentStore from "../../document/DocumentStore";
import { COMMANDS } from "../../document/commands";
import UserActions from "../../actions/UserActions";
import styles from "./SitePanelPageContextMenu.module.css";

const { CONTEXT_ITEM_BUTTON } = AppConstants;
const { EVENT_PAGE_DELETE } = UserEventConstants;

/**
 * Renders a dropdown menu for a page in the pages panel
 * @component
 * @param {Object} props -
 * @param {Object} props.site - The site object
 * @param {Object} props.page - The page object
 * @param {boolean} props.active - Indicates if the page is selected
 * @returns {React.ReactElement}
 */

const SitePanelPageContextMenu = ({ site, page, active }) => {
  const id = page.get("id");
  const isHome = page ? page.get("home") === 1 : false;
  const isWP = page ? page.get("wp") : false;
  const hasDomain = !!site.get("domain", false);
  const slug = page.get("slug");
  const publishable = page.get("publishable", true);

  const _duplicate = () => {
    if (page) {
      DocumentStore.dispatch({ type: COMMANDS.DUPLICATE_PAGE, payload: { id } });
      UserActions.trackEvent({ event: "page-duplicate", meta: { page: slug, id } });
    }
  };

  const _copyPageURL = async () => {
    const url = SiteUtils.createFullUrl(site, true, page);
    await copyToClipboard({ val: url || "", showToast: true });
  };

  const _setAsHome = () => {
    if (page) {
      DocumentStore.dispatch({ type: COMMANDS.SET_AS_HOME_PAGE, payload: { id } });
      UserActions.trackEvent({ event: "page-set-home", meta: { page: slug, id } });
    }
  };

  const _copyToWordpress = () => {
    if (page) {
      DocumentStore.dispatch({ type: COMMANDS.DUPLICATE_PAGE, payload: { id, toWP: true } });
      UserActions.trackEvent({ event: "page-to-wp", meta: { page: slug, id } });
    }
  };

  const _copyToPage = () => {
    if (page) {
      DocumentStore.dispatch({ type: COMMANDS.DUPLICATE_PAGE, payload: { id, toPage: true } });
      UserActions.trackEvent({
        event: "page-wp-to-page",
        meta: { page: slug, id },
      });
    }
  };

  const _togglePublishableState = (e) => {
    e.preventDefault();
    e.stopPropagation();
    DocumentStore.dispatch({
      type: COMMANDS.TOGGLE_PAGE_IS_PUBLISHABLE,
      payload: page.get("id"),
    });
  };

  const _removePageConfirmed = () => {
    if (page) {
      DocumentStore.dispatch({ type: COMMANDS.REMOVE_PAGE, payload: { id } });
      UserActions.trackEvent({ event: EVENT_PAGE_DELETE, meta: { page: slug, id } });
    }
  };

  const _removePage = () => {
    const title = page.get("name");

    UIActions.modalOpen(AppConstants.MODAL_ACTION_CONFIRM, {
      title: `Delete ${title}`,
      question: `Are you sure you want to delete the page '${title}'?`,
      message: "This removes a page from your design and cannot be undone.",
      confirmBtn: "Delete",
      confirmAction: _removePageConfirmed,
    });
  };

  const _rename = (event) => {
    const { target } = event;
    if (target?.closest(".no-autoclose")) {
      return;
    }

    UIActions.toggleRenamePopup({
      title: "Page",
      itemType: AppConstants.ITEM_TYPE_PAGE,
      itemId: id,
      name: page.get("name"),
      slug,
      showWarning: true,
    });
  };

  return (
    <ContextMenuWrapper icon="menuMoreHoriz" active={active}>
      <MenuItem data-e2e={uiPrefix(`rename-${CONTEXT_ITEM_BUTTON}`)} onClick={_rename}>
        Rename
      </MenuItem>
      <MenuItem data-e2e={uiPrefix(`duplicate-${CONTEXT_ITEM_BUTTON}`)} onClick={_duplicate}>
        Duplicate
      </MenuItem>
      <When it={hasDomain && !isWP}>
        <MenuItem
          data-e2e={uiPrefix(`copy-page-url-${CONTEXT_ITEM_BUTTON}`)}
          onClick={_copyPageURL}
        >
          Copy Page URL
        </MenuItem>
      </When>
      <When it={!(isHome || isWP)}>
        <MenuItem
          data-e2e={uiPrefix(`set-as-homepage-${CONTEXT_ITEM_BUTTON}`)}
          onClick={publishable ? _setAsHome : undefined}
          disabled={!publishable}
          className={!publishable && styles.disabled}
        >
          Set as Homepage
        </MenuItem>
      </When>
      <When it={!isWP}>
        <MenuItem
          data-e2e={uiPrefix(`copy-to-blog-${CONTEXT_ITEM_BUTTON}`)}
          onClick={_copyToWordpress}
        >
          Copy to Blog Template
        </MenuItem>
      </When>
      <When it={!(isHome || !isWP)}>
        <MenuItem data-e2e={uiPrefix(`copy-to-page-${CONTEXT_ITEM_BUTTON}`)} onClick={_copyToPage}>
          Copy to Page
        </MenuItem>
      </When>
      <Separator variant="context-menu" />
      <When it={!isHome}>
        <MenuItem data-e2e={uiPrefix(`publish-${CONTEXT_ITEM_BUTTON}`)} si-keep-open>
          <Switch
            className={styles["switch-label"]}
            label="Unpublish"
            checked={!publishable}
            onClick={_togglePublishableState}
            noPadding
          />
        </MenuItem>
        <Separator variant="context-menu" />
      </When>
      <MenuItem data-e2e={uiPrefix(`delete-${CONTEXT_ITEM_BUTTON}`)} onClick={_removePage}>
        <Box className={styles.delete}>Delete</Box>
      </MenuItem>
    </ContextMenuWrapper>
  );
};

export default SitePanelPageContextMenu;
